// PostgREST corta cada respuesta en `max_rows` (1000 en Supabase), aunque se pida más con
// `limit()`. Las listas largas (el alcance real tiene ~2700 tarjetas) se leen por rangos: la
// primera página trae el total (`count: 'exact'`) y el tamaño real de página; el resto se pide
// en paralelo.

import { ApiError, looksLikeNetworkMessage } from './errors.ts';

export interface RangeResponse<T> {
  data: T[] | null;
  error: { message: string } | null;
  count?: number | null;
}

export type FetchRange<T> = (from: number, to: number) => PromiseLike<RangeResponse<T>>;

function rowsOf<T>(response: RangeResponse<T>): T[] {
  if (response.error) {
    throw new ApiError(response.error.message, looksLikeNetworkMessage(response.error.message));
  }
  return response.data ?? [];
}

/** Lee todas las filas de una consulta ordenada, de a `pageSize` (o menos, si el servidor corta). */
export async function fetchAllRows<T>(fetchRange: FetchRange<T>, pageSize = 1000): Promise<T[]> {
  const first = await fetchRange(0, pageSize - 1);
  const rows = rowsOf(first);
  const total = first.count ?? rows.length;
  const size = rows.length;
  if (size === 0 || size >= total) return rows;
  const ranges: [number, number][] = [];
  for (let from = size; from < total; from += size) ranges.push([from, from + size - 1]);
  const pages = await Promise.all(ranges.map(([from, to]) => fetchRange(from, to)));
  return rows.concat(...pages.map(rowsOf));
}
